import React, { useMemo } from "react";
import { UserManagementStore } from "../../stores/UserManagementStore";
import { useDependencyInjector } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import type { UserManagementUser } from "../../types/UserManagementUser";
import { Table } from "@whatslly/ui-components/v2/Tables/Table/Table";
import type { TableColumn } from "@whatslly/ui-components/v2/Tables/Table/TableTypes";
import { ColoredName } from "../../../UserGroups/ColoredName";

interface UsersTableProps {
  loading?: boolean;
  error?: string;
  users: UserManagementUser[];
  hideRoles?: boolean;
  hideCrmActiveCol?: boolean;
  hideProfile?: boolean;
  hideTuvisProfile?: boolean;
  hidePlatform?: boolean;
  usersTableClassName?: string;
  disableActivate?: boolean;
}

export const UsersTable: React.FC<UsersTableProps> = observer(
  ({ error, loading, users, hideRoles, hideCrmActiveCol, usersTableClassName, hideProfile, hideTuvisProfile, hidePlatform, disableActivate }) => {
    const store = useDependencyInjector(UserManagementStore);
    const { t } = useTranslation();

    const [columns, overrideGridTemplateColumns] = useMemo(() => {
      const baseColumns: TableColumn<UserManagementUser>[] = [
        {
          title: "",
          keyInsideItem: `avatar`,
          getValue: (item) => <ColoredName name={item.name} />,
          disableSortBy: true,
        },
        { title: t("user_management.cols.name"), keyInsideItem: `name` },
        { title: t("user_management.cols.username"), keyInsideItem: `username` },
        { title: t("user_management.cols.phone"), keyInsideItem: `phone`, type: `phone`, disableSortBy: true },
        {
          title: t("user_management.cols.profile"),
          keyInsideItem: `profile`,
          type: `string`,
          disableSortBy: true,
        },
        {
          title: t("user_management.cols.roles"),
          keyInsideItem: `roles`,
          type: `tags`,
          disableSortBy: true,
        },
        { title: t("user_management.cols.created"), keyInsideItem: `createdAt`, type: `date` },
        { title: t("user_management.cols.last_login_desktop"), keyInsideItem: `lastDesktopLogin`, type: `date` },
        {
          title: t("user_management.cols.desktop_version"),
          keyInsideItem: `desktopVersion`,
          showByDefault: false,
          getValue: (user: UserManagementUser) => user.desktopVersion || "-",
        },
        {
          title: t("user_management.cols.active_sf"),
          disabled: () => true,
          keyInsideItem: `crmUserStatus`,
          type: `switch`,
          centerHeader: true,
          disableSortBy: true,
        },
        {
          title: t("user_management.cols.active_tuvis"),
          "data-testid": `tuvis-user-status-switch`,
          keyInsideItem: `tuvisUserStatus`,
          type: `switch`,
          onChange: (e) => {
            e && store.onUserStatusChange([e], !e.tuvisUserStatus);
          },
          centerHeader: true,
          disabled: () => disableActivate as any,
          disableSortBy: true,
        },
      ];

      const currentCols = baseColumns.filter(({ keyInsideItem }) => {
        switch (keyInsideItem) {
          case "roles":
            return !hideRoles;
          case "crmUserStatus":
            return !hideCrmActiveCol;
          case "profile":
            return !hideProfile;
          case "profileName":
            return (
              !hideTuvisProfile && (store.groupNames === undefined || store.groupNames?.case({ fulfilled: (groupNames) => groupNames.length === 0 }))
            );
          case "platform":
            return !hidePlatform;
          case "groupIds":
            return store.groupNames !== undefined;
          default:
            return true;
        }
      });

      const colsWidth = currentCols.map(({ keyInsideItem }) => {
        switch (keyInsideItem) {
          case "avatar":
            return "50px";
          case "name":
          case "username":
          case "groupIds":
            return "2fr";
          case "phone":
            return "150px";
          case "channels":
            return "2fr";
          default:
            return "1fr";
        }
      });
      const actionsMenuWidth = "20px";

      return [currentCols, `${colsWidth.join(" ")} ${actionsMenuWidth}`];
    }, [t, disableActivate, store, hideRoles, hideCrmActiveCol, hideProfile, hideTuvisProfile, hidePlatform]);

    return (
      <Table
        sort={store.sort}
        setSort={(sort) => store.setSort(sort)}
        className={usersTableClassName}
        items={users}
        error={error}
        loading={loading}
        loadMoreIsLoading={store.loadMoreResults?.state === "pending"}
        emptyStateNode={t("user_management.no_users_to_show")}
        getLink={(user) => store.getCrmLink(user)}
        loadMore={() => store.loadMore()}
        hasMoreResults={store.hasMoreResults}
        getActionsMenuActions={(user) =>
          [
            !user.crmUserId && {
              children: t("user_management.actions.resend_invite"),
              icon: "action-send-email",
              testId: "resend-invite",
              onClick: async (item?: UserManagementUser) => item && store.resendInvite([item]),
              active: () => true,
            },
          ].filter(Boolean) as any[]
        }
        columns={columns}
        overrideGridTemplateColumns={overrideGridTemplateColumns}
        searchTerm={store.searchTerm}
        searchEmptyStateNode={
          <>
            <div>{t("common.crm_table.no_results_found")}</div>
            <div>
              {t("common.crm_table.try_different_keywords")}
              <div>{t("common.crm_table.spelled_correctly")}</div>
            </div>
          </>
        }
      />
    );
  }
);
